import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { CircleCheck, ArrowLeft, Gauge, Clock, MapPin, Wrench, FileUp, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { SettingListItem } from '../../../../shared/types/setting.types';
import { VehicleServiceStatus, VehicleServiceType } from '../../../../shared/types/vehicle-service.types';
import useSettingLists from '../../wrappers/use-setting-list-item';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleService from '../../wrappers/use-vehicle-service';
import useFile from '../../wrappers/use-file';
import {
    serviceRequestHiddenInputStyles,
    serviceRequestStyles,
    serviceRequestUploadIconStyles,
} from './ServiceRequest.styles';

const ServiceRequest: React.FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const settingLists = useSettingLists();
    const { get: getVehicle } = useVehicle();
    const { create: createService } = useVehicleService();
    const { create: createFile } = useFile();
    const [dealers, setDealers] = useState<SettingListItem[]>([]);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [serviceTypes, setServiceTypes] = useState<SettingListItem[]>([]);
    const [loadingLists, setLoadingLists] = useState(true);
    const [formData, setFormData] = useState({
        dealerId: '',
        serviceType: '',
        description: '',
        currentMileage: '',
        currentHours: '',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!vin) return;
        getVehicle(vin).then((vehicle) => {
            if (vehicle?.odometer) {
                setFormData((prev) => ({
                    ...prev,
                    currentMileage: vehicle.odometer!.kilometers.toString(),
                    currentHours: vehicle.odometer!.motohours.toString(),
                }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin]);

    useEffect(() => {
        Promise.all([
            settingLists.list('dealers'),
            settingLists.list('serviceTypes'),
        ]).then(([dealerItems, serviceTypeItems]) => {
            setDealers(dealerItems.filter((i) => !i.blocked));
            setServiceTypes(serviceTypeItems.filter((i) => !i.blocked));
            setLoadingLists(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !vin) return;

        setSubmitting(true);
        setError(null);

        try {
            const service = await createService({
                createdBy: user.uid,
                vehicleUid: vin!,
                dealerId: formData.dealerId,
                serviceType: formData.serviceType as VehicleServiceType,
                status: VehicleServiceStatus.Ordered,
                description: formData.description,
                ...(formData.currentMileage && { currentMileage: Number(formData.currentMileage) }),
                ...(formData.currentHours && { currentHours: Number(formData.currentHours) }),
            });
            await Promise.all(attachments.map(file =>
                createFile(file, {
                    vehicleUid: vin!,
                    vehicleServiceUid: service.id,
                    garageUid: user.garageUid,
                    dealerId: formData.dealerId,
                })
            ));
            setIsSubmitted(true);
        } catch (err) {
            setError(t('serviceRequest.error'));
            console.error('Error submitting service request:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <Box sx={serviceRequestStyles.successRoot}>
                <Box sx={serviceRequestStyles.successIconWrap}>
                    <CircleCheck size={36} color="#2e7d32" />
                </Box>
                <Typography variant="h5" fontWeight={700} mb={1}>{t('serviceRequest.success.title')}</Typography>
                <Typography color="text.secondary" mb={4}>
                    {t('serviceRequest.success.description')}
                </Typography>
                <Button variant="contained" component={Link} to="/app/garage">
                    {t('serviceRequest.success.backButton')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={serviceRequestStyles.root}>
            <Box sx={serviceRequestStyles.header}>
                <IconButton component={Link} to={`/app/vehicles/${vin}`} size="small">
                    <ArrowLeft size={24} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>{t('serviceRequest.title')}</Typography>
            </Box>

            <Typography color="text.secondary" mb={3}>{t('serviceRequest.intro')}</Typography>

            <Paper elevation={0} sx={serviceRequestStyles.formPaper}>
                <Box component="form" onSubmit={handleSubmit} sx={serviceRequestStyles.form}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>{t('serviceRequest.vinLabel')}</Typography>
                        <Paper variant="outlined" sx={serviceRequestStyles.vinPaper}>
                            <Typography variant="body2" fontFamily="monospace">{vin}</Typography>
                        </Paper>
                    </Box>

                    <FormControl fullWidth required>
                        <InputLabel>{t('serviceRequest.dealerLabel')}</InputLabel>
                        <Select
                            label={t('serviceRequest.dealerLabel')}
                            value={formData.dealerId}
                            onChange={(e) => setFormData({ ...formData, dealerId: e.target.value })}
                            disabled={loadingLists}
                            startAdornment={
                                loadingLists
                                    ? <CircularProgress size={18} sx={serviceRequestStyles.selectLoadingAdornment} />
                                    : <InputAdornment position="start"><MapPin size={20} /></InputAdornment>
                            }
                        >
                            {dealers.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth required>
                        <InputLabel>{t('serviceRequest.serviceTypeLabel')}</InputLabel>
                        <Select
                            label={t('serviceRequest.serviceTypeLabel')}
                            value={formData.serviceType}
                            onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                            disabled={loadingLists}
                            startAdornment={
                                loadingLists
                                    ? <CircularProgress size={18} sx={serviceRequestStyles.selectLoadingAdornment} />
                                    : <InputAdornment position="start"><Wrench size={20} /></InputAdornment>
                            }
                        >
                            {serviceTypes.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={serviceRequestStyles.odometerGrid}>
                        <TextField
                            label={t('serviceRequest.kmLabel')}
                            type="number"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Gauge size={20} /></InputAdornment>, inputProps: { min: 0 } }}
                            value={formData.currentMileage}
                            onChange={(e) => setFormData({ ...formData, currentMileage: e.target.value })}
                        />
                        <TextField
                            label={t('serviceRequest.mthLabel')}
                            type="number"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Clock size={20} /></InputAdornment>, inputProps: { min: 0 } }}
                            value={formData.currentHours}
                            onChange={(e) => setFormData({ ...formData, currentHours: e.target.value })}
                        />
                    </Box>

                    <TextField
                        label={t('serviceRequest.description')}
                        multiline
                        rows={4}
                        placeholder={t('serviceRequest.descriptionPlaceholder')}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />

                    <Box>
                        <Typography variant="body2" fontWeight={500} mb={1.5}>{t('serviceRequest.attachments')}</Typography>
                        <Box
                            component="label"
                            htmlFor="file-upload-service"
                            sx={serviceRequestStyles.uploadZone}
                        >
                            <FileUp size={48} color="#bdbdbd" style={serviceRequestUploadIconStyles} />
                            <Typography variant="body2" color="primary.main" fontWeight={500}>{t('serviceRequest.uploadFiles')}</Typography>
                            <Typography variant="body2" color="text.secondary">{t('serviceRequest.dragHint')}</Typography>
                            <Typography variant="caption" color="text.disabled" mt={0.5}>{t('serviceRequest.sizeHint')}</Typography>
                            <input
                                id="file-upload-service"
                                type="file"
                                multiple
                                style={serviceRequestHiddenInputStyles}
                                onChange={(e) => {
                                    const files = Array.from(e.target.files ?? []);
                                    setAttachments(prev => [...prev, ...files]);
                                    e.target.value = '';
                                }}
                            />
                        </Box>
                        {attachments.length > 0 && (
                            <Box sx={serviceRequestStyles.attachments}>
                                {attachments.map((file, i) => (
                                    <Chip
                                        key={i}
                                        label={file.name}
                                        size="small"
                                        onDelete={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                                        deleteIcon={<X size={14} />}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}

                    <Box sx={serviceRequestStyles.actions}>
                        <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>{t('common.cancel')}</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !formData.dealerId || !formData.serviceType}
                            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                        >
                            {submitting ? t('serviceRequest.submitting') : t('serviceRequest.submit')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ServiceRequest;
